import React from "react"
import styled, { css } from "styled-components"
import { MobergButtonProps, StyledButtonProps } from "./Types"
import { MobergFontFamily, MobergFontSize } from "../MobergFont/MobergFont"
import { MobergAnimationCurve, MobergAnimationTiming } from "../MobergAnimation/MobergAnimation"
import { MobergTheme, getColors } from "../MobergThemes/MobergColors"

export enum MobergButtonVariant {
	FILLED = "filled",
	OUTLINE = "outline",
	CLEAR = "clear",
}

export enum MobergButtonShape {
	SQUARE = "square",
	RECTANGLE = "rectangle",
	WIDE = "wide",
	FULL_WIDTH = "full_width",
}

export const MobergButton: React.FC<MobergButtonProps> = React.forwardRef<HTMLButtonElement | null, MobergButtonProps>(({ children, theme, variant, shape, fontSize, tooltip, onClick, disabled, style, active, dataTestId }, ref) => {
	return (
		<div>
			{/* Wrapping the button in a div protects it from being stretched in flex containers. */}
			<BaseButton
				disabled={disabled}
				theme={theme ?? MobergTheme.BLACK}
				variant={variant ?? MobergButtonVariant.CLEAR}
				shape={shape ?? MobergButtonShape.RECTANGLE}
				fontSize={fontSize ?? MobergFontSize.REGULAR}
				active={active ?? false}
				title={tooltip}
				onMouseDown={onClick}
				style={style}
				ref={ref}
				data-testid={dataTestId}
			>
				{children}
			</BaseButton>
		</div>
	)
})

// Neutral themes use the body text color so they remain readable in both light and dark mode.
const NEUTRAL_THEMES = new Set([
	MobergTheme.BLACK,
	MobergTheme.GRAY,
	MobergTheme.MEDIUM_GRAY,
	MobergTheme.WHITE,
	MobergTheme.ON_BLUE,
	MobergTheme.ON_LIGHT_BLUE,
	MobergTheme.ON_GRAY,
])

const getButtonStyles = css<StyledButtonProps>`
	${({ theme, variant, active }) => {
		const colors = getColors(theme)
		const isNeutral = NEUTRAL_THEMES.has(theme)
		// For BLUE theme, use CSS variables so accent and hover backgrounds adapt to the color scheme.
		// Falls back to the hardcoded values in light mode (where the variables are undefined).
		const accentColor = theme === MobergTheme.BLUE
			? `var(--color-accent-interactive, ${colors.main})`
			: colors.main
		// In dark mode, BLUE hover/active uses a dark tonal bg so the #9FCAFF icon stays visible.
		const blueNoFillHover = theme === MobergTheme.BLUE
			? `var(--color-blue-hover-bg, ${colors.noFill.hover})`
			: colors.noFill.hover
		const blueNoFillActive = theme === MobergTheme.BLUE
			? `var(--color-blue-hover-bg, ${colors.noFill.active})`
			: colors.noFill.active

		switch (variant) {
			case MobergButtonVariant.FILLED:
				return css`
					background: ${colors.main};
					border: 2px solid ${colors.main};
					color: ${colors.text ?? "white"};
					&:hover:enabled {
						border-color: ${colors.hover};
						background: ${colors.hover};
					}
					&:active:enabled {
						border-color: ${colors.active};
						background: ${colors.active};
					}
				`
		case MobergButtonVariant.OUTLINE:
			if (theme === MobergTheme.RED) {
				return css`
					background: transparent;
					border: 2px solid ${colors.main};
					color: ${colors.main};
					&:hover:enabled {
						background: ${colors.main};
						border-color: ${colors.main};
						color: white;
					}
					&:active:enabled {
						background: ${colors.active};
						border-color: ${colors.active};
						color: white;
					}
				`
			}
			return css`
				background: transparent;
				border: 2px solid ${isNeutral ? "var(--text-color)" : accentColor};
				color: ${isNeutral ? "var(--text-color)" : accentColor};
				&:hover:enabled {
					background: ${isNeutral ? "var(--color-hover-neutral)" : blueNoFillHover};
				}
				&:active:enabled {
					background: ${isNeutral ? "var(--color-hover-active-neutral)" : blueNoFillActive};
				}
			`
		case MobergButtonVariant.CLEAR:
			return css`
				background: ${active ? (isNeutral ? "var(--color-hover-active-neutral)" : blueNoFillActive) : "transparent"};
				border: 2px solid transparent;
				color: ${isNeutral ? "var(--text-color)" : accentColor};
				&:hover:enabled {
					border-color: ${active
						? (isNeutral ? "var(--color-hover-active-neutral)" : blueNoFillActive)
						: (isNeutral ? "var(--color-hover-neutral)" : blueNoFillHover)};
					background: ${active
						? (isNeutral ? "var(--color-hover-active-neutral)" : blueNoFillActive)
						: (isNeutral ? "var(--color-hover-neutral)" : blueNoFillHover)};
				}
				&:active:enabled {
					border-color: ${isNeutral ? "var(--color-hover-active-neutral)" : blueNoFillActive};
					background: ${isNeutral ? "var(--color-hover-active-neutral)" : blueNoFillActive};
				}
			`
			default:
				return ""
		}
	}}
`

const getButtonPaddingFromShape = (shape?: MobergButtonShape) => {
	switch(shape) {
		case MobergButtonShape.WIDE: return "7px 32px"
		case MobergButtonShape.SQUARE: return "7px"
		case MobergButtonShape.RECTANGLE:
		default:
			return "7px 16px"
	}
}

const getButtonWidthFromShape = (shape?: MobergButtonShape) => {
	switch(shape) {
		case MobergButtonShape.FULL_WIDTH: return "100%"
		default:
			return ""
	}
}

const BaseButton = styled.button<StyledButtonProps>`
	display: flex;
	align-items: center;
	gap: 8px;
	justify-content: center;
	border: none;
	background: transparent;
	white-space: nowrap;
	font-style: normal;
	font-weight: bold;
	font-size: ${({ fontSize }) => fontSize};
	font-family: ${MobergFontFamily.HEADER};
	width: ${({ shape }) => getButtonWidthFromShape(shape)};
	padding: ${({ shape }) => getButtonPaddingFromShape(shape)};
	border-radius: 4px;
	transition: all ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN};

	${getButtonStyles}

	&:disabled {
		opacity: 0.3;
	}
`
